import { type RefObject, useEffect, useEffectEvent, useRef } from 'react';

/** How long a finger must rest on the handle before the row is "lifted" (a deliberate gesture). */
export const LIFT_DELAY_MS = 350;
/** A finger that travels farther than this before the delay is scrolling, not lifting. */
const SLOP_PX = 8;

export type RowEdge = 'top' | 'bottom';

export interface TouchLiftHandlers {
  /** The long press completed: the row is lifted. */
  onLift: () => void;
  /** The finger is over a row (or over none) at the given half. */
  onOver: (rowId: string | null, edge: RowEdge) => void;
  /** The finger was released (`null` target: released elsewhere, or the gesture was cancelled). */
  onRelease: (rowId: string | null, edge: RowEdge) => void;
}

/** The row under a point and which half of it the point is in. */
function rowAt(x: number, y: number): { id: string; edge: RowEdge } | null {
  const row = document.elementFromPoint(x, y)?.closest<HTMLElement>('[data-widget-row]');
  const id = row?.dataset['widgetRow'];
  if (!row || !id) return null;
  const box = row.getBoundingClientRect();
  return { id, edge: y < box.top + box.height / 2 ? 'top' : 'bottom' };
}

/**
 * Touch reordering through a handle: press and hold (`LIFT_DELAY_MS`) lifts the row, moving the
 * finger chooses the drop place, releasing drops. Before the hold a vertical swipe scrolls the page
 * as usual (the handle is `touch-action: pan-y`; the browser cancels the pointer then). After the
 * lift the page scroll is held with a non-passive `touchmove`. Mouse and pen are left to the native
 * drag of the pragmatic-dnd adapter. `consumedClick()` tells whether the click that trails a lift
 * must be ignored.
 */
export function useTouchLift(
  handle: RefObject<HTMLElement | null>,
  handlers: TouchLiftHandlers,
): { consumedClick: () => boolean } {
  const lift = useEffectEvent(handlers.onLift);
  const over = useEffectEvent(handlers.onOver);
  const release = useEffectEvent(handlers.onRelease);
  const flag = useRef(false);

  useEffect(() => {
    const element = handle.current;
    if (!element) return;
    let timer: number | undefined;
    let start: { x: number; y: number; id: number } | null = null;
    let lifted = false;

    const stopScroll = (event: TouchEvent) => {
      if (lifted && event.cancelable) event.preventDefault();
    };
    const end = (drop: boolean, x: number, y: number) => {
      window.clearTimeout(timer);
      window.removeEventListener('touchmove', stopScroll);
      if (lifted) {
        const target = drop ? rowAt(x, y) : null;
        release(target?.id ?? null, target?.edge ?? 'top');
        flag.current = true;
        window.setTimeout(() => {
          flag.current = false;
        }, 400);
      }
      lifted = false;
      start = null;
    };
    const onDown = (event: PointerEvent) => {
      if (event.pointerType !== 'touch' || start) return;
      start = { x: event.clientX, y: event.clientY, id: event.pointerId };
      timer = window.setTimeout(() => {
        lifted = true;
        window.addEventListener('touchmove', stopScroll, { passive: false });
        if ('vibrate' in navigator) navigator.vibrate(12);
        lift();
      }, LIFT_DELAY_MS);
    };
    const onMove = (event: PointerEvent) => {
      if (!start || event.pointerId !== start.id) return;
      if (!lifted) {
        if (Math.hypot(event.clientX - start.x, event.clientY - start.y) > SLOP_PX) {
          window.clearTimeout(timer); // a swipe: the page scrolls, nothing is lifted
          start = null;
        }
        return;
      }
      const target = rowAt(event.clientX, event.clientY);
      over(target?.id ?? null, target?.edge ?? 'top');
    };
    const onUp = (event: PointerEvent) => {
      if (start && event.pointerId === start.id) end(true, event.clientX, event.clientY);
    };
    const onCancel = (event: PointerEvent) => {
      if (start && event.pointerId === start.id) end(false, 0, 0);
    };
    const onMenu = (event: Event) => {
      if (start) event.preventDefault(); // the long press must not open a context menu
    };
    element.addEventListener('pointerdown', onDown);
    element.addEventListener('pointermove', onMove);
    element.addEventListener('pointerup', onUp);
    element.addEventListener('pointercancel', onCancel);
    element.addEventListener('contextmenu', onMenu);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('touchmove', stopScroll);
      element.removeEventListener('pointerdown', onDown);
      element.removeEventListener('pointermove', onMove);
      element.removeEventListener('pointerup', onUp);
      element.removeEventListener('pointercancel', onCancel);
      element.removeEventListener('contextmenu', onMenu);
    };
  }, [handle]);

  return { consumedClick: () => flag.current };
}
